/**
 * Client-side audit logging utilities
 * Handles logging of user actions and document access
 */

import { supabase } from './supabase';

export interface AuditLogEntry {
  userId?: string;
  entityType: 'document' | 'will' | 'auth' | 'session';
  entityId: string;
  action: string;
  status?: 'success' | 'failed' | 'pending';
  ipAddress?: string;
  userAgent?: string;
  details?: Record<string, any>;
  sessionId?: string;
}

/**
 * Log an audit event
 */
export const logAuditEvent = async (entry: AuditLogEntry): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    const { data, error } = await supabase.rpc('log_audit_event', {
      p_user_id: entry.userId || user?.id || null,
      p_entity_type: entry.entityType,
      p_entity_id: entry.entityId,
      p_action: entry.action,
      p_status: entry.status || 'success',
      p_ip_address: entry.ipAddress || null,
      p_user_agent: entry.userAgent || navigator.userAgent,
      p_details: entry.details || {},
      p_session_id: entry.sessionId || generateSessionId()
    });

    if (error) {
      console.error('Failed to log audit event:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Audit logging error:', error);
    return null;
  }
};

/**
 * Log document access (view, download, share, etc.)
 */
export const logDocumentAccess = async (
  documentId: string,
  action: 'view' | 'download' | 'share' | 'delete',
  details?: Record<string, any>
) => {
  return logAuditEvent({
    entityType: 'document',
    entityId: documentId,
    action,
    details: {
      ...details,
      timestamp: new Date().toISOString(),
      url: window.location.href
    }
  });
};

/**
 * Log will access and modifications
 */
export const logWillAccess = async (
  willId: string,
  action: 'view' | 'create' | 'update' | 'execute' | 'witness',
  details?: Record<string, any>
) => {
  return logAuditEvent({
    entityType: 'will',
    entityId: willId,
    action,
    details: {
      ...details,
      timestamp: new Date().toISOString(),
      url: window.location.href
    }
  });
};

/**
 * Log authentication events
 */
export const logAuthEvent = async (
  action: 'login' | 'logout' | 'signup' | 'password_reset',
  status: 'success' | 'failed' = 'success',
  details?: Record<string, any>
) => {
  const { data: { user } } = await supabase.auth.getUser();
  
  return logAuditEvent({
    userId: user?.id,
    entityType: 'auth',
    entityId: user?.id || 'anonymous',
    action,
    status,
    details: {
      ...details,
      timestamp: new Date().toISOString(),
      url: window.location.href
    }
  });
};

/**
 * Log session start
 */
export const logSessionStart = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) return null;

    const sessionId = generateSessionId();
    
    const { data, error } = await supabase.rpc('log_session_start', {
      p_user_id: user.id,
      p_session_id: sessionId,
      p_ip_address: null, // Will be captured server-side if available
      p_user_agent: navigator.userAgent
    });

    if (error) {
      console.error('Failed to log session start:', error);
      return null;
    }

    // Store session ID for later use
    sessionStorage.setItem('audit_session_id', sessionId);
    
    return data;
  } catch (error) {
    console.error('Session start logging error:', error);
    return null;
  }
};

/**
 * Log session end
 */
export const logSessionEnd = async (): Promise<boolean> => {
  try {
    const sessionId = sessionStorage.getItem('audit_session_id');
    
    if (!sessionId) return false;

    const { data, error } = await supabase.rpc('log_session_end', {
      p_session_id: sessionId
    });

    if (error) {
      console.error('Failed to log session end:', error);
      return false;
    }

    // Clear session ID
    sessionStorage.removeItem('audit_session_id');
    
    return data;
  } catch (error) {
    console.error('Session end logging error:', error);
    return false;
  }
};

/**
 * Generate a unique session ID
 */
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get current session ID
 */
export const getCurrentSessionId = (): string | null => {
  return sessionStorage.getItem('audit_session_id');
};

/**
 * Log suspicious activity detection
 */
export const logSuspiciousActivity = async (
  description: string,
  severity: 'low' | 'medium' | 'high' = 'medium',
  details?: Record<string, any>
) => {
  return logAuditEvent({
    entityType: 'auth',
    entityId: 'security',
    action: 'suspicious_activity',
    status: 'pending',
    details: {
      description,
      severity,
      ...details,
      timestamp: new Date().toISOString(),
      url: window.location.href
    }
  });
};

/**
 * Batch log multiple events (for performance)
 */
export const logBatchEvents = async (events: AuditLogEntry[]): Promise<void> => {
  try {
    const promises = events.map(event => logAuditEvent(event));
    await Promise.allSettled(promises);
  } catch (error) {
    console.error('Batch audit logging error:', error);
  }
};

/**
 * Hook for automatic page view logging
 */
export const useAuditPageView = (pageName: string) => {
  React.useEffect(() => {
    logAuditEvent({
      entityType: 'session',
      entityId: 'page_view',
      action: 'navigate',
      details: {
        page: pageName,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        referrer: document.referrer
      }
    });
  }, [pageName]);
};